import { ReactNode, useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { InformationOutlineIcon, LockLockedIcon, PushCenteredIcon } from '@arcade/web/icons/react'
import {
  Box,
  breakpoints,
  Button,
  Divider,
  Flex,
  FlexGrow,
  FlexNone,
  ListOrdered,
  spacing,
  Text,
} from '@cash-design-system/react'
import { ModalPortal } from '@cash-web/shared-feature-modal'
import { DistributionType } from '@cash-web/shared-feature-paychecks'

const PIP_WIDTH = '0.75rem'

const Pip = styled.div<{ color: string; size: string }>`
  background: ${({ color }) => color};
  border-radius: 50%;
  height: ${({ size }) => size};
  width: ${({ size }) => size};
`

const Clickable = styled.button`
  all: unset;
  border-radius: 0.25rem;
  cursor: pointer;
  display: block;
  padding: 0 0.75rem;
  width: calc(100% - 1.5rem);

  ${breakpoints.tablet} {
    ${({ theme }) => theme.selectors.keyboard} &:focus {
      border-radius: 0.5rem;
      outline: 0.5rem solid ${({ theme }) => theme.addOpacity(theme.colors.primary, 0.2)};
    }

    :hover {
      background-color: ${({ theme }) => theme.colors.secondaryButtonBackground};
    }
  }
`

type WrapperProps = {
  children: ReactNode
  onClick: (() => void) | undefined
}
const Wrapper = ({ children, onClick }: WrapperProps) =>
  onClick ? (
    <Clickable type="button" onClick={onClick}>
      {children}
    </Clickable>
  ) : (
    children
  )

type LegendListItemProps = {
  item: DistributionType
  type: 'percentage' | 'value'
  onEdit?: (type: DistributionType) => void
  flagZeroValues?: boolean
}

// centering the 16x16 lock icon the same as the 24x24 push icon
const LockIconContainer = styled.div`
  align-items: center;
  display: flex;
  padding: 0 0.25rem;
`
type CashBalanceInfoProps = { setIsCashBalanceInfoModalOpen: (isOpen: boolean) => void }
const CashBalanceInfo = ({ setIsCashBalanceInfoModalOpen }: CashBalanceInfoProps) => {
  const { t } = useTranslation()
  return (
    <Flex flexDirection="column" gap={spacing.xl}>
      <Text typography="labelMedium" color="textStandard" textAlign="center">
        {t('How it works', { context: 'title' })}
      </Text>
      <ListOrdered
        items={[
          <Text typography="body" key="first">
            {t(
              "Distribute your paycheck between savings, stocks, and bitcoin. This is any money you haven't taken an advance on.",
              { context: 'info text' }
            )}
          </Text>,
          <Flex flexDirection="column" gap={spacing.xs} key="second">
            <Text>{t('The rest will go directly to your Cash balance.', { context: 'info text' })}</Text>
            <Text typography="metaText" color="textSubtle">
              {t(
                'Stock distributions require at least $1 to trade. If your distribution is less, the amount will go back to your balance.',
                { context: 'info text' }
              )}
            </Text>
          </Flex>,
        ]}
      />
      <Flex gap={spacing.m} flexDirection="column">
        <Button size="large" variant="secondary" onClick={() => window.open('/support/DISTRIBUTE_PAYCHECK', '_blank')}>
          {t('Need help?', { context: 'cta button text' })}
        </Button>
        <Button size="large" variant="primary" onClick={() => setIsCashBalanceInfoModalOpen(false)}>
          {t('Done', { context: 'cta button text' })}
        </Button>
      </Flex>
    </Flex>
  )
}

const LegendListItem = ({ item, type, onEdit = undefined }: LegendListItemProps) => {
  const { colors } = useTheme()
  const [isCashBalanceInfoModalOpen, setIsCashBalanceInfoModalOpen] = useState(false)
  const openModal = () => setIsCashBalanceInfoModalOpen(true)

  let onClick = undefined
  if (item.variant === 'locked' && item.id === 'cashBalance') {
    onClick = openModal
  } else if (item.variant === 'actionable') {
    onClick = () => onEdit?.(item)
  }

  const pipColor =
    item.percentage === 0 && item.id !== 'cashBalance' && !item.flagZeroValues
      ? colors.secondaryButtonBackground
      : item.color

  return (
    <Wrapper onClick={onClick}>
      <Box padding="0.75rem 0">
        <Flex gap={spacing.xs} flexDirection="column">
          <Flex gap={spacing.m} alignItems="center">
            <FlexNone>
              <Pip color={pipColor} size="0.75rem"></Pip>
            </FlexNone>
            <FlexGrow>
              <Flex gap={spacing.xxs} alignItems="center">
                <Text
                  typography="labelMedium"
                  color={
                    item.id === 'deductions' || item.state === 'ERRORED'
                      ? 'textSubtle'
                      : item.percentage === 0
                        ? 'textSubtle'
                        : 'textStandard'
                  }
                >
                  {item.label}
                </Text>
                {item.id === 'cashBalance' && item.variant === 'locked' ? (
                  <>
                    <InformationOutlineIcon size={16} />
                    {isCashBalanceInfoModalOpen ? (
                      <ModalPortal closeButtonProps={{ onClose: () => setIsCashBalanceInfoModalOpen(false) }}>
                        <CashBalanceInfo setIsCashBalanceInfoModalOpen={setIsCashBalanceInfoModalOpen} />
                      </ModalPortal>
                    ) : null}
                  </>
                ) : null}
              </Flex>
            </FlexGrow>
            <Flex gap={spacing.s} alignItems="center">
              {item.percentage === 0 && item.flagZeroValues ? (
                <Box padding="0 xxs">
                  <Pip color={colors.error} size="0.5rem" />
                </Box>
              ) : null}
              <Text
                typography="body"
                color={
                  item.variant !== 'default' && (item.id === 'cashBalance' || item.percentage === 0)
                    ? 'textSubtle'
                    : 'textStandard'
                }
              >
                {item.value}
              </Text>
              {item.variant === 'locked' && (
                <Text typography="body" color="textSubtle">
                  <Flex>
                    <LockIconContainer>
                      <LockLockedIcon size={16} data-testid="locked-icon" />
                    </LockIconContainer>
                  </Flex>
                </Text>
              )}
              {item.variant === 'actionable' && (
                <Text typography="body" color="textSubtle">
                  <Flex>
                    <PushCenteredIcon size={24} />
                  </Flex>
                </Text>
              )}
            </Flex>
          </Flex>
          {item.note ? (
            <Box padding={`0 0 0 ${spacing.m}`}>
              <Box padding={`0 0 0 ${PIP_WIDTH}`}>
                <Text typography="helpText" color="textSubtle">
                  {item.note}
                </Text>
              </Box>
            </Box>
          ) : null}
        </Flex>
      </Box>
    </Wrapper>
  )
}

type DistributionListProps = {
  items: DistributionType[]
  type: 'percentage' | 'value'
  deductions?: DistributionType[]
}
export const PieChartLegend = ({ items, type, deductions = [] }: DistributionListProps) => {
  return (
    <Flex flexDirection="column" gap={spacing.xs}>
      {deductions.length ? (
        <div>
          {deductions.map(item => (
            <LegendListItem key={item.label} item={item} type={type} />
          ))}
        </div>
      ) : null}
      {deductions.length > 0 ? <Divider /> : null}
      <div>
        {items.map(item => (
          <LegendListItem
            key={item.label}
            item={item}
            type={type}
            onEdit={item.variant === 'actionable' ? item.action : undefined}
          />
        ))}
      </div>
    </Flex>
  )
}
